import { API_BASE } from "../config/constants";


const jsonRequest = async (url, options = {}) => {
  const response = await fetch(url, {
    headers: {
      "Content-Type": "application/json",
      ...(options.headers || {}),
    },
    ...options,
  });

  let payload = {};
  try {
    payload = await response.json();
  } catch (e) {
    payload = {};
  }

  if (!response.ok || payload?.success === false) {
    const details = payload?.errors;
    const message =
      payload?.message ||
      (details ? Object.values(details).join(", ") : null) ||
      "Inventory service failed";
    const error = new Error(message);
    error.details = details;
    error.status = response.status;
    throw error;
  }

  return payload;
};

const buildQuery = (params = {}) => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    if (typeof value === "boolean") {
      query.append(key, value ? 1 : 0);
      return;
    }
    query.append(key, value);
  });
  const qs = query.toString();
  return qs ? `?${qs}` : "";
};

export const inventoryApi = {
  getConfig: () => jsonRequest(`${API_BASE}/inventory/config`),

  getItems: ({ restaurantid, search, low_stock, recent, only_active = true }) =>
    jsonRequest(
      `${API_BASE}/inventory/items${buildQuery({
        restaurantid,
        search,
        low_stock,
        recent,
        only_active,
      })}`
    ),

  saveItem: (payload) =>
    jsonRequest(`${API_BASE}/inventory/items`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  getItem: ({ restaurantid, item_id }) =>
    jsonRequest(`${API_BASE}/inventory/items/${item_id}${buildQuery({ restaurantid })}`),

  updateItem: (itemId, payload) =>
    jsonRequest(`${API_BASE}/inventory/items/${itemId}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    }),

  stockIn: (payload) =>
    jsonRequest(`${API_BASE}/inventory/stock-in`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  getStockInEntries: ({ restaurantid, date, item_id }) =>
    jsonRequest(
      `${API_BASE}/inventory/stock-in${buildQuery({
        restaurantid,
        date,
        item_id,
      })}`
    ),

  updateStockInEntry: (entryId, payload) =>
    jsonRequest(`${API_BASE}/inventory/stock-in/${entryId}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    }),

  deleteStockInEntry: ({ entryId, restaurantid }) =>
    jsonRequest(
      `${API_BASE}/inventory/stock-in/${entryId}${buildQuery({
        restaurantid,
      })}`,
      {
        method: "DELETE",
      }
    ),

  stockOut: (payload) =>
    jsonRequest(`${API_BASE}/inventory/eod-used`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  getStockOutEntries: ({ restaurantid, date, item_id }) =>
    jsonRequest(
      `${API_BASE}/inventory/eod-used${buildQuery({
        restaurantid,
        date,
        item_id,
      })}`
    ),

  updateStockOutEntry: (entryId, payload) =>
    jsonRequest(`${API_BASE}/inventory/eod-used/${entryId}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    }),

  deleteStockOutEntry: ({ entryId, restaurantid }) =>
    jsonRequest(
      `${API_BASE}/inventory/eod-used/${entryId}${buildQuery({
        restaurantid,
      })}`,
      {
        method: "DELETE",
      }
    ),

  getSummary: ({ restaurantid, date }) =>
    jsonRequest(
      `${API_BASE}/inventory/summary${buildQuery({
        restaurantid,
        date,
      })}`
    ),

  getLedger: ({ restaurantid, item_id, from, to }) =>
    jsonRequest(
      `${API_BASE}/inventory/ledger${buildQuery({
        restaurantid,
        item_id,
        from,
        to,
      })}`
    ),

  reopenDay: (payload) =>
    jsonRequest(`${API_BASE}/inventory/day/reopen`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  getStockSummaryReport: ({ restaurantid, date, only_active }) =>
    jsonRequest(
      `${API_BASE}/inventory/reports/stock-summary${buildQuery({
        restaurantid,
        date,
        only_active,
      })}`
    ),

  getDailyUsageReport: ({ restaurantid, date, from, to }) =>
    jsonRequest(
      `${API_BASE}/inventory/reports/daily-usage${buildQuery({
        restaurantid,
        date,
        from,
        to,
      })}`
    ),

  getLowStockReport: ({ restaurantid, only_active }) =>
    jsonRequest(
      `${API_BASE}/inventory/reports/low-stock${buildQuery({
        restaurantid,
        only_active,
      })}`
    ),

  getItemPerformanceReport: ({ restaurantid, from, to, limit, sort }) =>
    jsonRequest(
      `${API_BASE}/inventory/reports/item-performance${buildQuery({
        restaurantid,
        from,
        to,
        limit,
        sort,
      })}`
    ),
};
